import { Route, Routes } from 'react-router-dom';
import AppRoutes from './routes';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createErpTheme } from './design-system';
import { PrivateRoute } from './routes/privateRoutes';
import { APP_AUTH } from './AppConfig';
import { PublicRoute } from './routes/publicRoutes';
import AuthLayout from './routes/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@/global/axios';
import { getLogoutPath } from './utils/common';
import ErrorAlert from './components/SnackBar/ErrorAlert';
import ServiceStatusScreen from './components/ServiceStatusScreen';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { getUserPrefConfig } from './views/login/queries';
import { setUserConfig } from './store/reducers/userConfig';

// No refetch on window focus (every tab switch re-ran every query on the
// page) and a single retry. Deliberately NO global staleTime: several screens
// reuse a query key for different fetchers ('config-details', ...), so cached
// data must stay opt-in per query.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

function App() {
  const darkMode = false;

  const { isSnackbarOpen, title, message, severity, details, action } = useSelector(
    (state: any) => state.snackbarState
  );

  const { fontSize } = useSelector(
    (state: any) => state.userConfig || { fontSize: 'medium' }
  );

  const theme = useMemo(
    () => createErpTheme(darkMode ? 'dark' : 'light', fontSize),
    [darkMode, fontSize]
  );

  const FONT_SIZE_MAP: Record<string, string> = {
    small: '14px',
    medium: '16px',
    large: '20px',
  };

  const dispatch = useDispatch();

  const { currentLoggedUserId } = useSelector(
    (state: any) => state.loginReducer || {}
  );

  useEffect(() => {
    if (currentLoggedUserId) {
      getUserPrefConfig()
        .then((resp: any) => {
          const body = resp?.data?.status !== undefined ? resp.data : resp;
          if (body?.status && body?.result) {
            const result = body.result;
            dispatch(
              setUserConfig({
                dateFormat: result.dateFormat,
                numberFormat: result.numFormat,
                fontSize: result.fontSize,
              } as any)
            );
          }
        })
        .catch((err) => {
          console.error('Failed to fetch user prefs on reload:', err);
        });
    }
  }, [currentLoggedUserId, dispatch]);

  useEffect(() => {
    const sizeKey = String(fontSize || 'medium').toLowerCase();
    document.documentElement.style.fontSize = FONT_SIZE_MAP[sizeKey] ?? '16px';
  }, [fontSize]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorAlert
            isOpen={isSnackbarOpen}
            title={title}
            message={message}
            severity={severity}
            details={details}
            action={action}
          />
          <ServiceStatusScreen />
          <Routes>
            <Route element={<PublicRoute />}>
              <Route path={`${APP_AUTH}/*`} element={<AuthLayout />} />
            </Route>
            <Route element={<PrivateRoute fallbackPath={getLogoutPath()} />}>
              <Route path="*" element={<AppRoutes />} />
            </Route>
          </Routes>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
